import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Search, FileText, ArrowLeft } from "lucide-react";
import Layout from "../components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OwnersList({ user, onLogout }) {
  const [owners, setOwners] = useState([]);
  const [filteredOwners, setFilteredOwners] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOwners();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = owners.filter(owner =>
        owner.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        owner.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOwners(filtered);
    } else {
      setFilteredOwners(owners);
    }
  }, [searchTerm, owners]);

  const fetchOwners = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/owners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOwners(response.data);
      setFilteredOwners(response.data);
    } catch (error) {
      toast.error("Error al cargar propietarios");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Cargando...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Propietarios</h1>
            <p className="text-gray-500 mt-1">Listado de todos los propietarios con contratos</p>
          </div>
          <Button variant="outline" onClick={() => navigate("/")} data-testid="back-to-dashboard-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Buscar por nombre o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-owners-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Owners Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredOwners.length === 0 ? (
            <div className="col-span-full text-center py-12 text-gray-500">
              No se encontraron propietarios
            </div>
          ) : (
            filteredOwners.map((owner) => (
              <Card
                key={owner.email}
                className="card-hover cursor-pointer"
                onClick={() => navigate(`/propietarios/${encodeURIComponent(owner.email)}`)}
                data-testid={`owner-card-${owner.email}`}
              >
                <CardHeader>
                  <CardTitle className="text-lg">{owner.nombre}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <span className="font-medium mr-2">Email:</span>
                      <span className="truncate">{owner.email}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <span className="font-medium mr-2">Teléfono:</span>
                      <span>{owner.telefono}</span>
                    </div>
                    <div className="flex items-center justify-between pt-2 mt-2 border-t">
                      <div className="flex items-center text-purple-600">
                        <FileText className="w-4 h-4 mr-1" />
                        <span className="font-medium">{owner.total_contratos}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {owner.total_contratos === 1 ? 'contrato' : 'contratos'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}
